import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { formatUrl, isExternalURL } from '../../utils/urlUtil';
import ContentItemFigure from './components/ContentItemFigure/ContentItemFigure';
import ContentItemInfo from './components/ContentItemInfo/ContentItemInfo';
import Placeholder from './components/Placeholder/Placeholder';
import VideoProgress from './components/VideoProgress/VideoProgress';
import { ContentItemLink, ContentItemWrapper } from './ContentItem.styles';
import { getSubtitle, getUrl } from './helpers';

const ContentItem = (props) => {
    const {
        className,
        index,
        content,
        loadImage = true,
        slidableListId,
        preferThumbnail,
        editModeActivated,
        markedAsDeleted,
        location,
        presentation,
        preferredAccessVersion,
        progress,
        onMarkedAsDeleted,
        onClick,
        itemPosition,
        isInSweden,
        forwardedRef,
        inView,
        autoStart = false,
    } = props;

    const [expanded, toggleExpanded] = useState(false);
    const [expansionIconVisible, toggleExpansionIconVisible] = useState(false);
    const [deletedItem, toggleDeletedItem] = useState(markedAsDeleted);

    if (content.placeholder) {
        return <Placeholder content={content} />;
    }

    const url = getUrl(content, preferredAccessVersion);

    const external = isExternalURL(url);

    const itemHeading = content.programTitle ? content.programTitle : content.title;

    let urlConfig = {};
    if (autoStart) {
        urlConfig.start = 'auto';
    }

    const onItemClick = (e) => {
        if (editModeActivated) {
            e.preventDefault();
            toggleDeletedItem(!deletedItem);
            onMarkedAsDeleted({
                videoId: content.id,
                titleArticleId: content.titleArticleId,
            });
        }
        if (onClick) {
            onClick(e, itemPosition, content.svtId);
        }
    };

    const onContentItemInfoClick = (e) => {
        if (onClick) {
            onClick(e, itemPosition, content.svtId);
        }
    };

    const onFocus = (e) => {
        toggleExpansionIconVisible(true);
    };

    const inGrid = typeof inView === 'boolean';

    const shouldRender = inView || !inGrid;
    const autoPlayOverlay = autoStart && !editModeActivated;

    return (
        <ContentItemWrapper className={className} ref={forwardedRef}>
            {/* Only render if content item is in view (intersection observer) */}
            {shouldRender && (
                <>
                    <ContentItemLink
                        isExternal={external}
                        to={!editModeActivated ? formatUrl(url, urlConfig) : ''}
                        aria-label={autoStart ? itemHeading + ' - starta video' : itemHeading}
                        tabIndex="0"
                        onAfterClick={(e) => onItemClick(e, index + 1, content.svtId)}
                        blurOnClick={editModeActivated}
                        onFocus={onFocus}
                    >
                        <ContentItemFigure
                            content={content}
                            preferThumbnail={preferThumbnail}
                            showPlaceholder={!loadImage}
                            fadeOut={deletedItem}
                            showBadges={!editModeActivated}
                            location={location}
                            presentation={presentation}
                            isInSweden={isInSweden}
                            autoPlayOverlay={autoPlayOverlay}
                            editModeActivated={editModeActivated}
                            markedAsDeleted={deletedItem}
                        />
                        <VideoProgress percent={progress} hidden={deletedItem} />
                    </ContentItemLink>
                    <ContentItemInfo
                        external={external}
                        url={url}
                        onContentInfoClick={onContentItemInfoClick}
                        title={itemHeading}
                        subtitle={getSubtitle(slidableListId, content)}
                        description={content.description}
                        editModeActivated={false}
                        index={index}
                        expanded={expanded}
                        toggleExpanded={toggleExpanded}
                        expansionIconVisible={expansionIconVisible}
                        toggleExpansionIconVisible={toggleExpansionIconVisible}
                    />
                </>
            )}
        </ContentItemWrapper>
    );
};

ContentItem.propTypes = {
    content: PropTypes.shape({
        title: PropTypes.string,
        programTitle: PropTypes.string,
        contentType: PropTypes.string,
        description: PropTypes.string,
        contentUrl: PropTypes.string,
        season: PropTypes.number,
        external: PropTypes.bool,
        live: PropTypes.bool,
        broadcastedNow: PropTypes.bool,
        broadcastEnded: PropTypes.bool,
        savedPosition: PropTypes.number,
    }).isRequired,
    index: PropTypes.number.isRequired,
    loadImage: PropTypes.bool,
    slidableListId: PropTypes.string,
    preferThumbnail: PropTypes.bool,
    editModeActivated: PropTypes.bool,
    markedAsDeleted: PropTypes.bool,
    location: PropTypes.string,
    presentation: PropTypes.string,
    preferredAccessVersion: PropTypes.string,
    progress: PropTypes.number,
    onMarkedAsDeleted: PropTypes.func,
    onClick: PropTypes.func,
};

export default React.memo(ContentItem);
